// Which rooms the landing showcase offers, and what each one promises.
//
// The pre-upsell personalises itself from two answers the user gave in
// onboarding — the GOAL picks the room, the PROBLEM picks the sentence and the
// curve worth drawing (upsell/content.ts). A visitor on the landing page has
// answered neither, so the tab strip stands in for both: picking a tab is
// picking a goal, and each goal ships with the problem that room is actually
// bought to solve.
//
// That pairing is the only thing this file decides. Everything else — the room,
// its name, its curves, the headline, the figure under it — is derived, so the
// landing and the in-product screen cannot say different things about the same
// room. Adding a fourth tab is one line here plus a room in rooms.ts.

import type { OnboardingGoalId, PlacementProblemId } from '@roomtreatment/room-flow';
import { personalise, type Personalised } from '@/components/upsell/content';
import { roomForGoal, type ShowcaseRoom } from '@/components/upsell/rooms';

export interface ShowcaseTabSpec {
  goal: OnboardingGoalId;
  problem: PlacementProblemId;
  /** What the tab is called at phone width, where three flexed tabs leave
   *  roughly 70px of text apiece and every room name ellipsises. Written here
   *  rather than in rooms.ts because it is a fit for THIS strip, not a second
   *  name for the room — the pre-upsell and the onboarding picker have space
   *  for the full one and should keep saying it. */
  short: string;
}

/** Order is the order on screen, and the first entry is what loads by default. */
export const SHOWCASE_TABS: readonly ShowcaseTabSpec[] = [
  { goal: 'listening-space', problem: 'bass', short: 'Hifi' },
  { goal: 'theater', problem: 'echo', short: 'Theater' },
  { goal: 'music-studio', problem: 'mixes', short: 'Studio' },
];

export interface ShowcaseSlice extends ShowcaseTabSpec {
  /** The room's own id — also the tab's `data-testid` suffix. Taken from the
   *  room rather than written here so a tab can never name a room it does not
   *  show. */
  id: string;
  label: string;
  /** `ShowcaseTabSpec.short`, under the name the strip reads it by. */
  shortLabel: string;
  room: ShowcaseRoom;
  copy: Personalised;
}

function sliceFor(spec: ShowcaseTabSpec): ShowcaseSlice {
  const room = roomForGoal(spec.goal);
  return {
    ...spec,
    id: room.id,
    label: room.name,
    shortLabel: spec.short,
    room,
    copy: personalise(spec.problem, room.curves),
  };
}

export const SHOWCASE_SLICES: readonly ShowcaseSlice[] = SHOWCASE_TABS.map(sliceFor);

export function showcaseSlice(id: string): ShowcaseSlice {
  return SHOWCASE_SLICES.find((s) => s.id === id) ?? SHOWCASE_SLICES[0];
}
